<template>
  <modal
    :value="value"
    max-width="640"
    @input="$emit('input', $event)"
  >
    <template #title>Mark as duplicate</template>
    <template #content>
      <div class="mark-duplicate">
        <div class="source mb-3">
          <div class="label">You're marking as a duplicate</div>
          <div class="title-text">
            {{ reference.publication.title }}
          </div>
          <div class="meta">
            {{ authorLine(reference) }}
            <v-chip
              x-small
              :color="statusFor(reference).color"
              text-color="white"
            >
              {{ statusFor(reference).text }}
            </v-chip>
          </div>
        </div>

        <v-text-field
          v-model="searchQuery"
          dense
          outlined
          hide-details
          clearable
          placeholder="Search all references by title, author or DOI…"
          prepend-inner-icon="search"
          class="mb-3"
          @input="onSearchInput"
        />

        <div class="label mb-1">
          {{
            searchActive ? 'Search results' : 'Suggested originals'
          }}
        </div>

        <div v-if="loading" class="empty">Loading…</div>
        <div v-else-if="displayItems.length === 0" class="empty">
          {{
            searchActive
              ? 'No matching references.'
              : 'No likely originals found — try searching.'
          }}
        </div>

        <v-radio-group
          v-else
          v-model="selectedId"
          hide-details
          class="mt-0"
        >
          <v-radio
            v-for="item in displayItems"
            :key="item.reference.id"
            :value="item.reference.id"
          >
            <template #label>
              <div class="candidate">
                <div class="candidate-title">
                  {{ item.reference.publication.title }}
                </div>
                <div class="candidate-meta">
                  {{ authorLine(item.reference) }}
                </div>
                <div class="candidate-chips">
                  <v-chip
                    v-if="item.confidence"
                    x-small
                    color="primary"
                    outlined
                  >
                    {{ confidenceLabel(item.confidence) }}
                  </v-chip>
                  <v-chip
                    x-small
                    :color="statusFor(item.reference).color"
                    text-color="white"
                  >
                    {{ statusFor(item.reference).text }}
                  </v-chip>
                </div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>

        <v-alert
          v-if="showScreenedWarning"
          dense
          outlined
          type="warning"
          class="mt-3 mb-0"
        >
          This reference has been screened — marking it a duplicate
          will hide it and its votes.
        </v-alert>

        <v-alert
          v-if="errorMessage"
          dense
          outlined
          type="error"
          class="mt-3 mb-0"
        >
          {{ errorMessage }}
        </v-alert>
      </div>
    </template>
    <template #actions>
      <v-btn text @click="$emit('input', false)">Cancel</v-btn>
      <v-btn
        color="primary"
        :disabled="!selectedId || submitting"
        :loading="submitting"
        @click="confirm"
      >
        Mark as duplicate
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/Common/Modal';
import { Reference } from '@/models';

const CONFIDENCE_LABELS = {
  DOI_MATCH: 'DOI match',
  HIGHLY_LIKELY: 'Highly likely',
  POTENTIAL: 'Possible',
};

export default {
  name: 'MarkAsDuplicateModal',
  components: { Modal },
  props: {
    value: { type: Boolean, default: false },
    reference: { type: Reference, required: true },
  },
  emits: ['input', 'marked'],
  data() {
    return {
      suggestions: [],
      searchResults: [],
      searchQuery: '',
      selectedId: null,
      loading: false,
      submitting: false,
      searchDebounce: null,
      errorMessage: null,
    };
  },
  computed: {
    searchActive() {
      return !!this.searchQuery && this.searchQuery.trim().length > 0;
    },
    displayItems() {
      if (this.searchActive) {
        return this.searchResults.map((reference) => ({
          reference,
          confidence: null,
        }));
      }
      return this.suggestions;
    },
    selectedTitle() {
      const selected = this.displayItems.find(
        (item) => item.reference.id === this.selectedId
      );
      return selected?.reference.publication.title ?? '';
    },
    showScreenedWarning() {
      const r = this.reference;
      return (
        r.isScreened() ||
        r.isPartiallyScreened() ||
        r.hasScreeningConflicts() ||
        r.hasScreeningOutcome()
      );
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        this.loadSuggestions();
      }
    },
  },
  created() {
    if (this.value) {
      this.loadSuggestions();
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounce);
  },
  methods: {
    reset() {
      this.searchQuery = '';
      this.searchResults = [];
      this.selectedId = null;
      this.errorMessage = null;
    },
    async loadSuggestions() {
      this.loading = true;
      try {
        this.suggestions = await this.$store.dispatch(
          'reference/getDuplicateSuggestions',
          {
            referenceId: this.reference.id,
            limit: 10,
          }
        );
      } catch (e) {
        this.errorMessage =
          "Couldn't load duplicate suggestions. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      // The candidate list is about to be replaced, so the current selection is
      // about to stop being visible. Keeping it would leave the confirm button
      // enabled with nothing selected on screen, and `selectedTitle` — which
      // only looks in `displayItems` — resolving to '', so the merge would be
      // confirmed against a reference the user cannot see and announced as
      // Merged "…" into "".
      this.selectedId = null;
      if (!this.searchActive) {
        this.searchResults = [];
        return;
      }
      this.searchDebounce = setTimeout(this.runSearch, 300);
    },
    async runSearch() {
      this.loading = true;
      try {
        const results = await this.$store.dispatch(
          'reference/searchReferencesInReview',
          {
            reviewId: this.reference.review.id,
            searchInput: this.searchQuery.trim(),
          }
        );
        this.searchResults = results.filter(
          (r) => r.id !== this.reference.id
        );
      } catch (e) {
        this.errorMessage =
          "Couldn't search references. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async confirm() {
      if (!this.selectedId) return;
      this.submitting = true;
      this.errorMessage = null;
      try {
        await this.$store.dispatch('reference/resolveDuplicates', {
          forReferenceId: this.selectedId,
          removeReferenceIds: [this.reference.id],
        });
        this.$emit('marked', {
          referenceId: this.reference.id,
          referenceTitle: this.reference.publication.title,
          originalId: this.selectedId,
          originalTitle: this.selectedTitle,
        });
        this.$emit('input', false);
      } catch (e) {
        this.errorMessage =
          "Couldn't mark as duplicate. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
    confidenceLabel(confidence) {
      return CONFIDENCE_LABELS[confidence] || confidence;
    },
    authorLine(reference) {
      const p = reference.publication;
      const authors = (p.authors || []).slice(0, 3).join(', ');
      return [authors, p.year, p.doi].filter(Boolean).join(' · ');
    },
    statusFor(reference) {
      if (reference.hasScreeningOutcome()) {
        return reference.hasRelevantScreeningOutcome()
          ? { text: 'Included', color: 'success' }
          : { text: 'Excluded', color: 'error' };
      }
      if (reference.isScreened())
        return { text: 'Screened', color: 'primary' };
      if (reference.hasScreeningConflicts())
        return { text: 'Conflict', color: 'warning' };
      if (reference.isPartiallyScreened())
        return { text: 'In progress', color: 'primary' };
      return { text: 'Unscreened', color: 'grey' };
    },
  },
};
</script>

<style lang="scss" scoped>
.mark-duplicate {
  .label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
  }
  .source .title-text {
    font-weight: 600;
  }
  .meta,
  .candidate-meta {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
  }
  .candidate-title {
    font-weight: 600;
  }
  .candidate-chips {
    display: flex;
    gap: 6px;
    margin-top: 2px;
  }
  .empty {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    padding: 8px 0;
  }
}
</style>
